import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Collapse,
  FormControlLabel,
  Grid,
  Paper,
  Switch,
  TextField,
  Typography,
  MenuItem,
} from '@mui/material';
import { atendimentoService } from '../../services/atendimentoService';

interface DaySchedule {
  enabled: boolean;
  start: string;
  end: string;
}

interface BusinessHours {
  monday: DaySchedule;
  tuesday: DaySchedule;
  wednesday: DaySchedule;
  thursday: DaySchedule;
  friday: DaySchedule;
  saturday: DaySchedule;
  sunday: DaySchedule;
}

const defaultBusinessHours: BusinessHours = {
  monday: { enabled: true, start: '08:00', end: '18:00' },
  tuesday: { enabled: true, start: '08:00', end: '18:00' },
  wednesday: { enabled: true, start: '08:00', end: '18:00' },
  thursday: { enabled: true, start: '08:00', end: '18:00' },
  friday: { enabled: true, start: '08:00', end: '18:00' },
  saturday: { enabled: false, start: '08:00', end: '12:00' },
  sunday: { enabled: false, start: '08:00', end: '12:00' },
};

const defaultOutOfHoursMessage = `👋 Olá! Seja bem-vindo à **Nome do Provedor** 🦊📶

⏰ No momento, estamos fora do nosso horário de atendimento.

💬 Deixe sua mensagem por aqui e nossa equipe retornará assim que possível.

🙏 Obrigado pela compreensão! 💙
**Nome do Provedor — O futuro começa aqui! 🚀**`;

const weekDays: { key: keyof BusinessHours; label: string }[] = [
  { key: 'monday', label: 'Segunda-feira' },
  { key: 'tuesday', label: 'Terca-feira' },
  { key: 'wednesday', label: 'Quarta-feira' },
  { key: 'thursday', label: 'Quinta-feira' },
  { key: 'friday', label: 'Sexta-feira' },
  { key: 'saturday', label: 'Sabado' },
  { key: 'sunday', label: 'Domingo' },
];

const AtendimentoExpedienteTab: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [businessHoursEnabled, setBusinessHoursEnabled] = useState(false);
  const [businessHours, setBusinessHours] = useState<BusinessHours>(defaultBusinessHours);
  const [outOfHoursMessageText, setOutOfHoursMessageText] = useState('');
  const [ignorarSaudacao, setIgnorarSaudacao] = useState(false);
  const [quandoEnviar, setQuandoEnviar] = useState<'novo_ticket' | 'nova_mensagem'>('novo_ticket');

  useEffect(() => {
    let ativo = true;

    const carregar = async () => {
      try {
        setLoading(true);
        const config = await atendimentoService.getConfiguracao();
        if (!ativo) return;

        setBusinessHoursEnabled(!!config.expediente_ativo);
        setBusinessHours(config.expediente_horarios || defaultBusinessHours);
        setOutOfHoursMessageText(config.texto_fora_expediente || '');
        setIgnorarSaudacao(!!config.expediente_ignorar_saudacao);
        setQuandoEnviar(config.expediente_quando_enviar === 'nova_mensagem' ? 'nova_mensagem' : 'novo_ticket');
      } catch (err: any) {
        if (ativo) setErrorMsg(err.message || 'Erro ao carregar expediente do atendimento.');
      } finally {
        if (ativo) setLoading(false);
      }
    };

    carregar();

    return () => {
      ativo = false;
    };
  }, []);

  const updateDaySchedule = (day: keyof BusinessHours, field: keyof DaySchedule, value: boolean | string) => {
    setBusinessHours((current) => ({
      ...current,
      [day]: {
        ...current[day],
        [field]: value,
      },
    }));
  };

  const salvar = async () => {
    try {
      setSaving(true);
      setErrorMsg('');
      setSuccessMsg('');

      await atendimentoService.updateConfiguracao({
        expediente_ativo: businessHoursEnabled,
        expediente_horarios: businessHours,
        texto_fora_expediente: outOfHoursMessageText,
        expediente_ignorar_saudacao: ignorarSaudacao,
        expediente_quando_enviar: quandoEnviar,
      });

      setSuccessMsg('Horario de expediente atualizado com sucesso.');
    } catch (err: any) {
      setErrorMsg(err.message || 'Erro ao salvar expediente do atendimento.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {successMsg && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMsg('')}>{successMsg}</Alert>}
      {errorMsg && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrorMsg('')}>{errorMsg}</Alert>}

      <Grid container spacing={1.5}>
        <Grid item xs={12}>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Horario de Expediente
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Configure os horarios de funcionamento para cada dia da semana.
          </Typography>

          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>Prioridade de verificacao:</strong> o sistema verifica primeiro o horario de expediente configurado na <strong>conexao</strong>.
              Se nao estiver habilitado na conexao, entao verifica o horario configurado aqui no <strong>atendimento</strong>.
            </Typography>
            <Box component="br" />
            Com o controle de expediente desativado nos dois lugares, o atendimento funcionara 24 horas por dia, 7 dias por semana.
          </Alert>

          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={businessHoursEnabled}
                color="primary"
                onChange={(event) => setBusinessHoursEnabled(event.target.checked)}
              />
            }
            label={<Typography variant="body2" fontWeight={500}>Ativar controle de expediente</Typography>}
          />
        </Grid>

        <Grid item xs={12}>
          <Collapse in={businessHoursEnabled}>
            <Box sx={{ mt: 2 }}>
              {weekDays.map((day) => {
                const daySchedule = businessHours[day.key] || defaultBusinessHours[day.key];
                return (
                  <Paper
                    key={day.key}
                    variant="outlined"
                    sx={{
                      p: 1, px: 1.5,
                      mb: 0.5,
                      bgcolor: daySchedule.enabled ? 'transparent' : 'action.disabledBackground',
                    }}
                  >
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} sm={4}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={daySchedule.enabled}
                              onChange={(event) => updateDaySchedule(day.key, 'enabled', event.target.checked)}
                              color="success"
                              size="small"
                            />
                          }
                          label={
                            <Typography
                              variant="body2"
                              fontWeight={500}
                              color={daySchedule.enabled ? 'text.primary' : 'text.secondary'}
                            >
                              {day.label}
                            </Typography>
                          }
                        />
                      </Grid>
                      <Grid item xs={5} sm={3}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Inicio"
                          type="time"
                          value={daySchedule.start}
                          onChange={(event) => updateDaySchedule(day.key, 'start', event.target.value)}
                          disabled={!daySchedule.enabled}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      <Grid item xs={2} sm={1} sx={{ textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                          as
                        </Typography>
                      </Grid>
                      <Grid item xs={5} sm={3}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Termino"
                          type="time"
                          value={daySchedule.end}
                          onChange={(event) => updateDaySchedule(day.key, 'end', event.target.value)}
                          disabled={!daySchedule.enabled}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                    </Grid>
                  </Paper>
                );
              })}

              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  Mensagem Fora do Expediente
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Mensagem enviada quando o cliente entra em contato fora do horario de atendimento.
                </Typography>

                <TextField
                  value={outOfHoursMessageText}
                  onChange={(event) => setOutOfHoursMessageText(event.target.value)}
                  placeholder="Digite a mensagem que sera enviada fora do horario..."
                  label="Texto da mensagem fora do expediente"
                  helperText="Voce pode usar emojis."
                  size="small"
                  fullWidth
                  multiline
                  minRows={4}
                />

                {!outOfHoursMessageText && (
                  <Button
                    size="small"
                    sx={{ mt: 1 }}
                    onClick={() => setOutOfHoursMessageText(defaultOutOfHoursMessage)}
                  >
                    Usar mensagem padrao
                  </Button>
                )}
              </Box>

              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  Comportamento Fora do Expediente
                </Typography>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={ignorarSaudacao}
                          onChange={(e) => setIgnorarSaudacao(e.target.checked)}
                          color="primary"
                          size="small"
                        />
                      }
                      label={<Typography variant="body2">Não enviar mensagem de saudação</Typography>}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      select
                      fullWidth
                      size="small"
                      label="Enviar mensagem quando:"
                      value={quandoEnviar}
                      onChange={(e) => setQuandoEnviar(e.target.value as 'novo_ticket' | 'nova_mensagem')}
                    >
                      <MenuItem value="novo_ticket">Um novo ticket for criado</MenuItem>
                      <MenuItem value="nova_mensagem">Nova mensagem recebida</MenuItem>
                    </TextField>
                  </Grid>
                </Grid>
              </Box>
            </Box>
          </Collapse>
        </Grid>

        <Grid item xs={12}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 1 }}>
            <Button variant="contained" size="small" disabled={saving} onClick={salvar}>
              {saving ? 'Salvando...' : 'Salvar'}
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AtendimentoExpedienteTab;
